import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy-page">
      <div className="privacy-content">
        <h1>Privacy Policy for Lift Coach AI</h1>
        <p><strong>Effective Date: October 21st, 2025</strong></p>
        
        <h2>Overview</h2>
        <p>Lift Coach AI ("the App") is committed to protecting your privacy. This privacy policy explains our data practices for the App, which uses your device's camera to analyze exercise form, range of motion, and tempo.</p>
        
        <h2>Information Collection and Use</h2>
        
        <h3>Camera Usage</h3>
        <p>The App requires access to your device's camera to:</p>
        <ul>
          <li>Analyze your exercise form in real-time</li>
          <li>Record videos of your workout sets</li>
          <li>Provide feedback on range of motion and tempo</li>
        </ul>
        
        <h3>Video Storage</h3>
        <p>All recorded workout videos are stored locally on your device by default. These videos:</p>
        <ul>
          <li>Are never automatically uploaded to external servers</li>
          <li>Are never shared with third parties without your explicit consent</li>
          <li>Remain under your complete control</li>
          <li>Can be deleted at any time through the App or your device's storage settings</li>
        </ul>
        
        <h3>Optional Video Sharing (Beta Users Only)</h3>
        <p>During the beta testing phase, you may choose to share workout videos with our development team to help improve the App. If you opt to share a video:</p>
        <ul>
          <li>You must manually select and upload each video—no automatic uploads occur</li>
          <li>Videos are securely transmitted to Firebase Cloud Storage</li>
          <li>Videos are used solely for quality assurance and app improvement purposes</li>
          <li>Videos are accessible only to our development team</li>
          <li>Videos are retained for a limited time and then permanently deleted</li>
          <li>You can choose which videos to share and are never required to upload any videos</li>
        </ul>
        
        <h3>Analytics Data</h3>
        <p>The App uses Google Firebase Analytics to collect usage data to help us improve the App experience. This includes:</p>
        <ul>
          <li>App usage events (e.g., app opens, feature usage)</li>
          <li>Workout data (exercise type, number of repetitions, duration, and form scores)</li>
          <li>Device information (e.g., device model, operating system version)</li>
          <li>General location data (country/region level only)</li>
        </ul>
        
        <p>This analytics data:</p>
        <ul>
          <li>Does not include any personally identifiable information</li>
          <li>Does not include video content or camera data (unless you explicitly choose to upload a video as described above)</li>
          <li>Does not include your name, email, or contact information</li>
          <li>Is collected and processed in accordance with Google's Privacy Policy</li>
          <li>Can be limited through your device's privacy settings</li>
        </ul>
        
        <h2>Data Security</h2>
        <p>Video data and exercise analysis remain entirely on your device unless you explicitly choose to upload a video. All data transmitted to Firebase (including optional video uploads and analytics) is encrypted in transit and subject to Google's security practices.</p>
        <p>We recommend using device passcodes and keeping your iOS updated.</p>
        
        <h2>Third-Party Services</h2>
        <p>The App uses Google Firebase for:</p>
        <ul>
          <li>Analytics (Firebase Analytics)</li>
          <li>Optional video storage (Firebase Cloud Storage, beta only)</li>
        </ul>
        <p>Firebase may collect and process data as described in <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>. No other third-party services have access to your data.</p>
        
        <h2>Your Privacy Choices</h2>
        <p>You can:</p>
        <ul>
          <li>Choose whether to upload any workout videos</li>
          <li>Limit analytics data collection through your device's privacy settings (iOS Settings &gt; Privacy &amp; Security &gt; Analytics &amp; Improvements)</li>
          <li>Delete the App and all local data at any time</li>
        </ul>
        
        <h2>Children's Privacy</h2>
        <p>The App does not knowingly collect any information from users under the age of 13.</p>
        
        <h2>Changes to This Policy</h2>
        <p>We may update this privacy policy from time to time. Any changes will be reflected with an updated effective date.</p>
        
        <h2>Contact Information</h2>
        <p>For questions about this privacy policy or the App's privacy practices, please contact Lift Coach AI Support at <a href="mailto:support@getliftcoach.com">support@getliftcoach.com</a>.</p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
